/**
 * Papéis num condomínio (síndico, subsíndico, conselheiro, zelador, morador, proprietário, contato
 * da administradora) — o mesmo padrão CRUD de relações/empregos, agora condomínio↔pessoa: busca a
 * pessoa (com cadastro rápido se ainda não existe), escolhe o papel, e o vínculo nasce pronto. Cada
 * papel vira aresta `PAPEL_EM` no grafo; o síndico mantém histórico (o mandato anterior é fechado
 * pelo backend ao abrir um novo).
 */
import { Building, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ComboBuscaEntidade,
  type ConfigBusca,
  type ItemBusca,
} from '@/components/crud/ComboBuscaEntidade';
import { CamposVinculo, periodoLegivel } from '@/components/crud/CamposVinculo';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Chip } from '@/components/ui/misc';
import { api, type PapelCondominioCad } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

//: Vocabulário `PapelCondominio` do backend → rótulo humano.
const PAPEIS: { valor: string; rotulo: string }[] = [
  { valor: 'sindico', rotulo: 'Síndico' },
  { valor: 'subsindico', rotulo: 'Subsíndico' },
  { valor: 'conselheiro', rotulo: 'Conselheiro' },
  { valor: 'zelador', rotulo: 'Zelador' },
  { valor: 'morador', rotulo: 'Morador' },
  { valor: 'proprietario', rotulo: 'Proprietário' },
  { valor: 'contato_administradora', rotulo: 'Contato da administradora' },
];
const rotuloPapel = (v: string) => PAPEIS.find((p) => p.valor === v)?.rotulo ?? v;

const BUSCA_PESSOA: ConfigBusca = {
  endpoint: '/cadastro/pessoas',
  rotulo: (p) => String(p.nome ?? ''),
  sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
  placeholder: 'Buscar pessoa por nome ou CPF…',
  camposRapido: [
    { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
    { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
  ],
};

export function PainelPapeisCondominio({ condominioId }: { condominioId: string }) {
  const [papeis, setPapeis] = useState<PapelCondominioCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [pessoa, setPessoa] = useState<ItemBusca | null>(null);
  const [papel, setPapel] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null); // pessoa/papel/inicio

  async function carregar() {
    const r = await api.get<PapelCondominioCad[]>(`/cadastro/condominios/${condominioId}/papeis`);
    setCarregando(false);
    if (r.ok) setPapeis(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominioId]);

  function limparForm() {
    setPessoa(null);
    setPapel('');
    setInicio('');
    setFim('');
    setObservacoes('');
  }

  async function adicionar(): Promise<boolean> {
    if (!pessoa || !papel || salvando) return false;
    setSalvando(true);
    const r = await api.post('/cadastro/condominios/papeis', {
      condominio_id: condominioId,
      pessoa_id: pessoa.id,
      papel,
      data_inicio: inicio || null,
      data_fim: fim || null,
      observacoes: observacoes.trim() || null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success(`${String(pessoa.nome)} vinculado como ${rotuloPapel(papel)}.`);
    limparForm();
    void carregar();
    return true;
  }

  function chaveDe(p: PapelCondominioCad) {
    return `${p.pessoa_id}/${p.papel}/${p.data_inicio}`;
  }

  async function remover(p: PapelCondominioCad) {
    const r = await api.del(
      `/cadastro/condominios/papeis/${condominioId}/${p.pessoa_id}/${p.papel}/${p.data_inicio}`,
    );
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Papel removido.');
    void carregar();
  }

  async function salvarEdicao(p: PapelCondominioCad, fim: string, obs: string) {
    const r = await api.patch(
      `/cadastro/condominios/papeis/${condominioId}/${p.pessoa_id}/${p.papel}/${p.data_inicio}`,
      { data_fim: fim || null, observacoes: obs.trim() || null },
    );
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Papel atualizado.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="size-4 text-accent" />
          Síndico, moradores e demais papéis
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Quem é quem no condomínio. Cada papel vira aresta no grafo; ao trocar de síndico, o mandato
          anterior é fechado e vira histórico.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, papeis.length === 0, 'Nenhum papel registrado ainda.') ?? (
          <div className="flex flex-col gap-2">
            {papeis.map((p) => {
              const chave = chaveDe(p);
              return editando === chave ? (
                <FormEdicaoPapel
                  key={chave}
                  papel={p}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(fim, obs) => void salvarEdicao(p, fim, obs)}
                />
              ) : (
                <div
                  key={chave}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] text-text">{p.pessoa.nome}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {p.pessoa.cpf && (
                        <span className="tipo-dado">{formatar(p.pessoa.cpf, 'cpf')} · </span>
                      )}
                      {periodoLegivel(p.data_inicio, p.data_fim)}
                      {p.observacoes && ` · ${p.observacoes}`}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Chip aceso>{rotuloPapel(p.papel)}</Chip>
                    <Button
                      variant="fantasma"
                      size="sm"
                      onClick={() => setEditando(chave)}
                      aria-label="Editar"
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="fantasma"
                      size="sm"
                      className="text-danger hover:text-danger"
                      onClick={() => void remover(p)}
                      aria-label="Remover"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo papel no condomínio"
          descricao="Busque a pessoa (ou cadastre-a na hora) e escolha o papel. Aparece também no cadastro da pessoa e no grafo."
          rotuloAbrir="Adicionar papel"
          rotuloSalvar="Vincular"
          podeSalvar={!!pessoa && !!papel}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Pessoa</span>
            <ComboBuscaEntidade config={BUSCA_PESSOA} onSelecionar={setPessoa} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Papel</span>
            <select
              value={papel}
              onChange={(e) => setPapel(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              <option value="">Escolha o papel</option>
              {PAPEIS.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
          <CamposVinculo
            inicio={inicio}
            fim={fim}
            observacoes={observacoes}
            onInicio={setInicio}
            onFim={setFim}
            onObservacoes={setObservacoes}
          />
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}

/** Edição in-place do papel: só fim e observação (pessoa/papel/início são a identidade). */
function FormEdicaoPapel({
  papel,
  onSalvar,
  onCancelar,
}: {
  papel: PapelCondominioCad;
  onSalvar: (fim: string, obs: string) => void;
  onCancelar: () => void;
}) {
  const [fim, setFim] = useState(papel.data_fim ?? '');
  const [obs, setObs] = useState(papel.observacoes ?? '');
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">
        {papel.pessoa.nome} — {rotuloPapel(papel.papel)}
      </span>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Até</span>
          <input
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
          />
        </label>
        <label className="flex flex-1 flex-col gap-2">
          <span className="tipo-rotulo">Observações</span>
          <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="…" />
        </label>
        <Button type="button" variant="secundario" onClick={() => onSalvar(fim, obs)}>
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}
